import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Dropdown from 'components/form/Dropdown';
import SeachInput from 'components/form/SeachInput';
import Card from 'components/Layout/Card';
import Table from 'components/Layout/Table';

import { fetchAllTemplateData, setAllTemplateData, throwError } from 'store/slices';
import { getDataFromLocalStorage, objectToFormData } from 'utils/helpers';
import usePermission from 'hooks/usePermission';
import { debounce } from 'lodash';

const EmailTemplateSideBar = ({ onSelectTemplate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user_type } = getDataFromLocalStorage();

  // Redux state
  const { allTemplateData } = useSelector((state) => state.global) || {};

  // Permissions
  const { viewAccess } = usePermission("Email Templates");

  // Component state
  const [filterData, setFilterData] = useState({
    total: 0,
    offset: 0,
    limit: 10,
    status: '',
  });

  const [searchPayload, setSearchPayload] = useState({ subject: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Fetch all templates
  const getAllTemplateData = async (params = {}) => {
    setIsLoading(true);

    const payload = { ...filterData, ...searchPayload, ...params };
    const formData = objectToFormData(payload);
    const response = await dispatch(fetchAllTemplateData(formData));

    if (response?.data) {
      const templates = response.data.template || [];
      dispatch(setAllTemplateData(templates));

      setFilterData((prev) => ({
        ...prev,
        ...params,
        total: response.data.result_count || 0,
      }));

      // Auto-select first template if available
      if (templates.length > 0) {
        onSelectTemplate(templates[0]);
        setSelectedTemplateId(templates[0].id);
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (["0", "1"].includes(user_type)) {
      getAllTemplateData();
    } else {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handleTemplateClick = (elem) => {
    if (viewAccess === "1") {
      setSelectedTemplateId(elem.id);
      onSelectTemplate(elem);
    } else {
      dispatch(throwError({ message: "You do not have permission to view" }));
    }
  };

  const handlePagination = (offset) => getAllTemplateData({ offset });

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        getAllTemplateData({ offset: 0, subject: value });
      }, 500),
    []
  );

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchPayload((prev) => ({ ...prev, subject: value }));
    debouncedSearch(value);
  };

  const handleStatusChange = (e) => {
    const { value } = e.target;
    setFilterData((prev) => ({ ...prev, status: value, offset: 0 }));
    getAllTemplateData({ status: value, offset: 0 });
  };

  // Table rows
  const rowData = allTemplateData?.map((elem) => {
    const { id, subject, status } = elem;
    const isSelected = selectedTemplateId === id;

    return {
      isSelected,
      data: [
        {
          value: (
            <div
              className="text-truncate d-flex justify-content-start cps-10 pointer"
              onClick={() => handleTemplateClick(elem)}
              title={subject}
            >
              {subject}
              {/* {subject.length > 40 ? subject.slice(0, 40) + "..." : subject} */}
            </div>
          ),
        },
        {
          value: (
            <div
              className={`cps-8 cpt-3 cpb-3 cpe-8 text-12-500-15 br-4 ${status === 1
                ? isSelected ? 'color-white bg-text-blue b-white' : 'color-text-blue bg-eff3 b-text-blue'
                : 'color-6866 bg-F3F4F8 b-6866'
                }`}
            >
              {status === 1 ? 'Enabled' : 'Disabled'}
            </div>
          ),
        },
      ],
    };
  });

  return (
    <Card className="cpb-20">
      {/* Search & Filter */}
      <div className="cps-16 cpe-16 border-bottom">
        <div className="cpt-13 text-14-400-18 color-title-navy">
          Top Performers :
        </div>

        <div className="cpt-23 cpb-15 d-flex">
          <div className="me-2">
            <SeachInput
              placeholder="Search Subjects"
              value={searchPayload.subject}
              onChange={handleSearch}
              className="small-text-input icon-left"
              iconClassName="small-search-icon left"
            />
          </div>

          <Dropdown
            id="template_status"
            placeholder="Template Status"
            options={[
              { id: '1', name: 'Enabled' },
              { id: '0', name: 'Disabled' },
            ]}
            value={filterData.status}
            optionValue="name"
            onChange={handleStatusChange}
            className="small-dropdown"
          />
        </div>
      </div>

      {/* Template Table */}
      <Table
        isLoading={isLoading}
        header={[]}
        rowData={rowData}
        filterData={filterData}
        searchPayload={searchPayload}
        changeOffset={handlePagination}
        isOtherPagination
        pageRangeDisplayed={1}
      />
    </Card>
  );
};

export default EmailTemplateSideBar;
